import { z } from 'zod';

// Browser-safe contracts of the Governance module (zod only).

export const MeetingKind = z.enum(['annual', 'special', 'board']);
export const MeetingStatus = z.enum(['draft', 'convened', 'held', 'closed', 'cancelled']);
export const ItemKind = z.enum(['procedure', 'information', 'discussion', 'resolution', 'election']);
export const ItemSource = z.enum(['statutory', 'request', 'obligation', 'work_order', 'follow_up', 'proposal', 'manual']);
export const Majority = z.enum(['simple', 'absolute', 'double', 'triple', 'unanimous']);
export type LegalStructure = 'divided_coownership' | 'npo_with_servitudes';

const text = (max: number) => z.string().trim().min(1).max(max);

export const CreateMeeting = z.object({
  kind: MeetingKind,
  title: text(200),
  /** ISO date-time (local time is chosen in the app). */
  scheduledAt: z.iso.datetime({ offset: true }),
  location: z.string().trim().max(300).default(''),
});
export type CreateMeeting = z.input<typeof CreateMeeting>;

export const UpdateMeeting = z.object({
  title: text(200).optional(),
  scheduledAt: z.iso.datetime({ offset: true }).optional(),
  location: z.string().trim().max(300).optional(),
  generalNotes: z.string().max(20_000).optional(),
  minutes: z.string().max(100_000).optional(),
  status: z.enum(['held', 'cancelled']).optional(),
});
export type UpdateMeeting = z.input<typeof UpdateMeeting>;

export const AgendaItemInput = z.object({
  title: text(300),
  description: z.string().trim().max(5000).default(''),
  kind: ItemKind.default('discussion'),
  source: ItemSource.default('manual'),
  sourceId: z.uuid().optional(),
  proposedResolution: z.string().trim().max(5000).default(''),
  majority: Majority.optional(),
});
export type AgendaItemInput = z.input<typeof AgendaItemInput>;

export const UpdateAgendaItem = z.object({
  title: text(300).optional(),
  description: z.string().trim().max(5000).optional(),
  kind: ItemKind.optional(),
  proposedResolution: z.string().trim().max(5000).optional(),
  majority: Majority.nullable().optional(),
  notes: z.string().max(20_000).optional(),
  decision: z.string().max(5000).optional(),
});
export type UpdateAgendaItem = z.input<typeof UpdateAgendaItem>;

export const ActionInput = z.object({
  description: text(1000),
  responsible: z.string().trim().max(200).default(''),
  dueOn: z.iso.date().optional(),
});
export type ActionInput = z.input<typeof ActionInput>;

export interface AgendaItem {
  id: string;
  position: number;
  title: string;
  description: string;
  kind: z.infer<typeof ItemKind>;
  source: z.infer<typeof ItemSource>;
  sourceId: string | null;
  fixedEnd: boolean;
  proposedResolution: string;
  majority: z.infer<typeof Majority> | null;
  notes: string;
  decision: string;
  /** Each attendee's choice, by attendance id (resolutions). */
  votes: Record<string, z.infer<typeof VoteChoice>>;
  /** The result once the vote is closed. */
  voteResult: VoteResult | null;
}

export interface Action {
  id: string;
  meetingId: string;
  meetingTitle: string;
  description: string;
  responsible: string;
  dueOn: string | null;
  status: 'open' | 'done' | 'cancelled';
  doneOn: string | null;
}

export interface MeetingSummary {
  id: string;
  kind: z.infer<typeof MeetingKind>;
  title: string;
  scheduledAt: string;
  location: string;
  status: z.infer<typeof MeetingStatus>;
  minutesStatus: 'none' | 'draft' | 'approved';
}

export const AttendanceStatus = z.enum(['present', 'proxy', 'absent']);
export const VoteChoice = z.enum(['for', 'against', 'abstain']);
export type VoteBasis = 'quote_parts' | 'lots' | 'persons';

export const UpdateAttendance = z.object({ status: AttendanceStatus, proxyHolder: z.string().trim().max(200).default('') });
export type UpdateAttendance = z.input<typeof UpdateAttendance>;
export const RecordVotes = z.object({ choices: z.array(z.object({ attendanceId: z.uuid(), choice: VoteChoice.nullable() })).max(2000) });
export type RecordVotes = z.input<typeof RecordVotes>;

export interface Attendee {
  id: string;
  partyId: string | null;
  userId: string | null;
  name: string;
  /** Units held (labels), for a co-owner. */
  units: string;
  votes: number;
  status: z.infer<typeof AttendanceStatus>;
  proxyHolder: string;
  /** Present but not entitled to vote (e.g. contributions unpaid for more than three months). */
  votingSuspended: boolean;
  suspensionReason: string;
  /** Chairs the meeting (board meetings: their casting vote breaks a tie when the by-laws give it). */
  isChair: boolean;
}

export interface Quorum {
  presentVotes: number;
  totalVotes: number;
  presentCount: number;
  /** Share of all votes present or represented (0–1). */
  ratio: number;
  reached: boolean;
  /** The quorum required (0–1). */
  threshold: number;
}

export interface VoteResult {
  majority: z.infer<typeof Majority>;
  basis: VoteBasis;
  for: number;
  against: number;
  abstain: number;
  /** Votes of the people present or represented who may vote. */
  eligibleVotes: number;
  totalVotes: number;
  forCount: number;
  eligibleCount: number;
  totalVoters: number;
  adopted: boolean;
  /** The tie was broken by the chair's casting vote. */
  castingVote?: boolean;
}

export interface Meeting extends MeetingSummary {
  convenedAt: string | null;
  generalNotes: string;
  minutes: string;
  minutesApprovedAt: string | null;
  minutesDocumentId: string | null;
  items: AgendaItem[];
  actions: Action[];
  /** The attendance register, once prepared (empty before). */
  attendance: Attendee[];
  voteBasis: VoteBasis | null;
  totalVotes: number | null;
  totalVoters: number | null;
  quorum: Quorum | null;
  /** Board meeting whose rules give the chair a casting vote. */
  castingVote: boolean;
  /** The notice rules that apply to this meeting (days before it), and whether today is within them. */
  notice: NoticeRule & { daysBefore: number; withinPeriod: boolean };
}

/** Notice period for a meeting, in days before it. Defaults to validate with a lawyer; the declaration or by-laws may differ. */
export interface NoticeRule {
  minDays: number | null;
  maxDays: number | null;
}

/** Something the agenda agent may put on the agenda, gathered from the rest of the platform. */
export interface AgendaCandidate {
  source: Exclude<z.infer<typeof ItemSource>, 'statutory' | 'manual' | 'proposal'>;
  sourceId: string;
  title: string;
  detail: string;
}

/** An agenda item proposed by the agent, for the board to add (or not). */
export interface ProposedAgendaItem extends AgendaItemInput {
  reason: string;
}

// --- Quorum and majorities (pure: used by the server to record results and by the app to show them live).
// Defaults from the Civil Code of Québec for a syndicate (to validate with a lawyer; the declaration may differ):
// quorum = majority of all votes present or represented (art. 1089); simple majority = majority of the votes of the
// co-owners present or represented (art. 1096); double majority = majority in number of the co-owners present or
// represented, holding three quarters of their votes (art. 1097); triple majority = three quarters of all co-owners
// holding 90 % of all votes (art. 1098); absolute majority = majority of all votes. For an association, the same
// computations on lots by default (its by-laws decide). Board meetings: one vote per director.

const attending = (a: Pick<Attendee, 'status'>) => a.status === 'present' || a.status === 'proxy';

/**
 * Quorum: 50 % means a majority (more than half, as the Civil Code says for a syndicate); any other percentage means
 * "at least" that share of all votes (as by-laws usually say).
 */
export function quorumOf(attendance: Pick<Attendee, 'status' | 'votes'>[], totalVotes: number, quorumPercent = 50): Quorum {
  const present = attendance.filter(attending);
  const presentVotes = present.reduce((s, a) => s + a.votes, 0);
  const ratio = totalVotes > 0 ? presentVotes / totalVotes : 0;
  const reached = quorumPercent === 50 ? ratio > 0.5 : ratio * 100 >= quorumPercent - 1e-9;
  return { presentVotes, totalVotes, presentCount: present.length, ratio, reached, threshold: quorumPercent / 100 };
}

export function tallyVote(
  attendance: Pick<Attendee, 'id' | 'status' | 'votes' | 'votingSuspended'>[],
  choices: Record<string, z.infer<typeof VoteChoice>>,
  context: { majority: z.infer<typeof Majority>; basis: VoteBasis; totalVotes: number; totalVoters: number; castingVote?: z.infer<typeof VoteChoice> | null },
): VoteResult {
  const eligible = attendance.filter((a) => attending(a) && !a.votingSuspended);
  const sum = (choice: z.infer<typeof VoteChoice>) => eligible.filter((a) => choices[a.id] === choice).reduce((s, a) => s + a.votes, 0);
  const result = {
    majority: context.majority,
    basis: context.basis,
    for: sum('for'),
    against: sum('against'),
    abstain: sum('abstain'),
    eligibleVotes: eligible.reduce((s, a) => s + a.votes, 0),
    totalVotes: context.totalVotes,
    forCount: eligible.filter((a) => choices[a.id] === 'for').length,
    eligibleCount: eligible.length,
    totalVoters: context.totalVoters,
  };
  const eps = 1e-9;
  const adopted = (() => {
    switch (context.majority) {
      case 'simple':
        return result.for > result.eligibleVotes / 2 + eps;
      case 'absolute':
        return result.for > result.totalVotes / 2 + eps;
      case 'double':
        return result.forCount > result.eligibleCount / 2 && result.for >= 0.75 * result.eligibleVotes - eps;
      case 'triple':
        return result.forCount >= 0.75 * result.totalVoters && result.for >= 0.9 * result.totalVotes - eps;
      case 'unanimous':
        return result.eligibleCount > 0 && result.forCount === result.eligibleCount;
    }
  })();
  // A tie on a simple majority: the chair's casting vote decides, when the by-laws give one (board meetings).
  if (context.majority === 'simple' && result.eligibleCount > 0 && result.for === result.against && (context.castingVote === 'for' || context.castingVote === 'against')) {
    return { ...result, adopted: context.castingVote === 'for', castingVote: true };
  }
  return { ...result, adopted: result.eligibleCount > 0 && adopted };
}

/** The decision as written in the minutes (French: the syndicate's records are in French). */
export function describeResult(r: VoteResult): string {
  const label = { simple: 'majorité simple', absolute: 'majorité absolue', double: 'double majorité', triple: 'triple majorité', unanimous: 'unanimité' }[r.majority];
  if (r.forCount === r.eligibleCount && r.eligibleCount > 0) return r.adopted ? 'Adoptée à l’unanimité des voix exprimées.' : `Rejetée : unanimité des présents en faveur, mais ${label} non atteinte.`;
  const fmt = (n: number) =>
    r.basis === 'quote_parts'
      ? `${new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 2 }).format(r.totalVotes ? (n / r.totalVotes) * 100 : 0)} % des voix`
      : `${new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 2 }).format(n)} voix`;
  const casting = r.castingVote ? ', voix prépondérante du président' : '';
  return `${r.adopted ? 'Adoptée' : 'Rejetée'} (${label}${casting}) : pour, ${fmt(r.for)}; contre, ${fmt(r.against)}; abstentions, ${fmt(r.abstain)}.`;
}

/** An assembly as co-owners see it: date, place and agenda (no notes, no votes). */
export interface MemberAssembly {
  id: string;
  kind: 'annual' | 'special';
  title: string;
  scheduledAt: string;
  location: string;
  status: z.infer<typeof MeetingStatus>;
  items: Pick<AgendaItem, 'position' | 'title' | 'description' | 'kind' | 'proposedResolution' | 'majority'>[];
  minutesPublished: boolean;
}

// --- Recordings and transcripts

export const RecordingStatus = z.enum(['recording', 'uploaded', 'transcribing', 'transcribed', 'failed', 'purged']);

export interface Recording {
  id: string;
  meetingId: string;
  source: 'browser' | 'upload' | 'transcript';
  status: z.infer<typeof RecordingStatus>;
  mimeType: string;
  language: 'fr-CA' | 'en-US';
  chunkCount: number;
  bytes: number;
  durationMs: number | null;
  error: string | null;
  /** Speaker labels renamed by people, e.g. { "spk_0": "Présidente" }. */
  speakerNames: Record<string, string>;
  startedAt: string;
  finishedAt: string | null;
  transcribedAt: string | null;
  markers: { itemId: string; offsetMs: number }[];
  segments: { seq: number; speaker: string; startMs: number; endMs: number; text: string }[];
}

export const StartRecording = z.object({
  mimeType: z.string().trim().max(100).default(''),
  language: z.enum(['fr-CA', 'en-US']).default('fr-CA'),
});
export type StartRecording = z.input<typeof StartRecording>;
export const RecordingMarker = z.object({ itemId: z.uuid(), offsetMs: z.number().int().min(0).max(24 * 3_600_000) });
export const SpeakerNames = z.object({ speakerNames: z.record(z.string().max(50), z.string().trim().max(100)) });

// --- Meeting rules of the client (by-laws)

export const GovernanceRulesInput = z
  .object({
    voteBasis: z.enum(['quote_parts', 'lots', 'persons']),
    quorumPercent: z.number().min(1).max(100),
    noticeMinDays: z.number().int().min(0).max(90).nullable(),
    noticeMaxDays: z.number().int().min(1).max(180).nullable(),
    /** Board meetings: quorum of directors (50: a majority), the chair's casting vote, notice to directors (days). */
    boardQuorumPercent: z.number().min(1).max(100).default(50),
    boardCastingVote: z.boolean().default(false),
    boardNoticeDays: z.number().int().min(0).max(60).nullable().default(null),
  })
  .refine((r) => r.noticeMaxDays === null || r.noticeMinDays === null || r.noticeMaxDays >= r.noticeMinDays, { message: 'The maximum must be at least the minimum', path: ['noticeMaxDays'] });
export type GovernanceRulesInput = z.input<typeof GovernanceRulesInput>;

export interface GovernanceRules extends z.output<typeof GovernanceRulesInput> {
  /** True when no rule was set: the defaults of the legal structure apply. */
  isDefault: boolean;
}

/**
 * Defaults by legal structure (to validate with a lawyer). Syndicate: quote-parts, quorum = majority of all votes
 * (C.c.Q. art. 1089), notice 10 to 30 days. Association: one vote per lot, quorum and notice from its by-laws (to set).
 */
export function defaultRules(structure: LegalStructure): GovernanceRules {
  return structure === 'divided_coownership'
    ? { voteBasis: 'quote_parts', quorumPercent: 50, noticeMinDays: 10, noticeMaxDays: 30, ...BOARD_DEFAULTS, isDefault: true }
    : { voteBasis: 'lots', quorumPercent: 50, noticeMinDays: 10, noticeMaxDays: null, ...BOARD_DEFAULTS, isDefault: true };
}
const BOARD_DEFAULTS = { boardQuorumPercent: 50, boardCastingVote: false, boardNoticeDays: null };

// --- Calendar of regular meetings

export const SeriesEvery = z.enum(['week', 'two_weeks', 'month', 'two_months', 'quarter']);
export type SeriesEvery = z.infer<typeof SeriesEvery>;

/** The dates of a series of regular meetings (YYYY-MM-DD). Monthly on the 31st falls back to the month's last day. */
export function seriesDates(first: string, every: SeriesEvery, count: number): string[] {
  const [y, m, d] = first.split('-').map(Number) as [number, number, number];
  const months = { month: 1, two_months: 2, quarter: 3 } as Record<string, number>;
  return Array.from({ length: count }, (_, i) => {
    if (every === 'week' || every === 'two_weeks') {
      const date = new Date(Date.UTC(y, m - 1, d + i * (every === 'week' ? 7 : 14)));
      return date.toISOString().slice(0, 10);
    }
    const month = m - 1 + i * months[every]!;
    const last = new Date(Date.UTC(y, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, month, Math.min(d, last))).toISOString().slice(0, 10);
  });
}

export const CreateSeries = z.object({
  kind: MeetingKind,
  title: z.string().trim().min(1).max(200),
  location: z.string().trim().max(300).default(''),
  scheduledAts: z.array(z.iso.datetime({ offset: true })).min(1).max(24),
});
export type CreateSeries = z.input<typeof CreateSeries>;

// --- Written resolutions (signed by all the directors instead of a meeting)

export const WrittenResolutionInput = z.object({
  title: z.string().trim().min(1).max(200),
  resolution: z.string().trim().min(1).max(10_000),
});
export type WrittenResolutionInput = z.input<typeof WrittenResolutionInput>;

export interface WrittenResolution {
  id: string;
  title: string;
  resolution: string;
  status: 'draft' | 'circulating' | 'adopted' | 'cancelled';
  circulatedAt: string | null;
  adoptedAt: string | null;
  createdAt: string;
  signatures: { userId: string; name: string; signedAt: string | null }[];
}

// --- Registers

/** A decision in the register of resolutions: a vote at a meeting, or a written resolution. */
export interface RegisterEntry {
  /** Year and order within it, e.g. 2026-03. */
  number: string;
  date: string;
  source: 'meeting' | 'written';
  meetingKind: z.infer<typeof MeetingKind> | null;
  meetingId: string | null;
  meetingTitle: string | null;
  resolutionId: string | null;
  title: string;
  text: string;
  decision: string;
  adopted: boolean;
}

/** An entry of the minute book: approved minutes, or an adopted written resolution (kept with the minutes). */
export interface MinuteBookEntry {
  date: string;
  kind: z.infer<typeof MeetingKind> | 'written';
  id: string;
  title: string;
  documentId: string | null;
  approvedAt: string | null;
}
